import { useState } from 'react';
import {
  CUSTOM_FLAG_COLORS, CUSTOM_FLAG_ICONS, CUSTOM_TASK_RULE, PERSON_FLAG_TYPES, TASK_PRIORITY_RULES, defaultTaskRule, taskPriorityLabel,
  type FlagTypeInput, type FlagTypeRow, type TaskPriorityRule,
} from '../../lib/flags';

/**
 * A custom flag type (#316): a name, an icon and a colour from fixed lists,
 * whether it can be addressed, and what its reply is called. Used in the
 * Flag menu's "New type..." and in Settings. For a built-in (#332) only the
 * name, icon and colour can change.
 *
 * With `taskRules` (Settings, #337) it also edits "When made into a task":
 * a priority, a tag with the flag's name, and for types that name a person a
 * follow-up with them. Each shows the type's default; a value equal to the
 * default is saved as null, so the type keeps following the default.
 */
export function FlagTypeForm({ initial, submitLabel = 'Add type', builtIn = false, typeId, taskRules = false, onSubmit, onCancel }: {
  initial?: Partial<FlagTypeRow>;
  submitLabel?: string;
  builtIn?: boolean;
  /** The type being edited, for its task defaults; none for a new custom type. */
  typeId?: string;
  taskRules?: boolean;
  onSubmit: (input: FlagTypeInput) => Promise<void>;
  onCancel: () => void;
}) {
  const defaults = typeId && builtIn ? defaultTaskRule(typeId) : CUSTOM_TASK_RULE;
  const personType = !!typeId && PERSON_FLAG_TYPES.has(typeId);
  const [priority, setPriority] = useState<TaskPriorityRule | ''>(initial?.task_priority ?? '');
  const [tag, setTag] = useState(initial?.task_tag ?? defaults.tag);
  const [followUp, setFollowUp] = useState(initial?.task_follow_up ?? defaults.followUp);
  const [name, setName] = useState(initial?.name ?? '');
  const [icon, setIcon] = useState(initial?.icon ?? 'flag');
  const [color, setColor] = useState(initial?.color ?? CUSTOM_FLAG_COLORS[0]);
  const [addressable, setAddressable] = useState(initial?.addressable ?? true);
  const [replyLabel, setReplyLabel] = useState(initial?.reply_label ?? 'Note');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { setError('Give the new type a name.'); return; }
    setBusy(true);
    try {
      const rules = taskRules ? {
        task_priority: priority === '' ? null : priority,
        task_tag: tag === defaults.tag ? null : tag,
        // Not shown for a type without a person: keep whatever is stored.
        task_follow_up: !personType ? initial?.task_follow_up ?? null : followUp === defaults.followUp ? null : followUp,
      } : {};
      await onSubmit(builtIn
        ? { name: name.trim(), icon, color, ...rules } as FlagTypeInput
        : { name: name.trim(), icon, color, addressable, reply_label: replyLabel.trim() || 'Note', ...rules });
    } catch {
      setError('Could not save the type. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full rounded-md border border-border-hover bg-bg-input px-2 py-1 text-sm text-text-primary';
  return (
    <form className="flex flex-col gap-2 px-1.5 pb-1.5 pt-1 text-xs text-text-secondary" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <label className="flex flex-col gap-1">Name
        <input className={field} value={name} maxLength={60} placeholder="e.g. Client request" onChange={(e) => { setName(e.target.value); setError(''); }} />
      </label>
      <span>Icon</span>
      <div className="flex flex-wrap gap-1">
        {Object.entries(CUSTOM_FLAG_ICONS).map(([key, Icon]) => (
          <button key={key} type="button" aria-label={`Icon ${key}`} aria-pressed={icon === key} onClick={() => setIcon(key)}
            className={`grid h-7 w-7 place-items-center rounded-md border ${icon === key ? 'border-accent-hover bg-bg-hover text-text-primary' : 'border-border-hover text-text-secondary'}`}>
            <Icon size={15} aria-hidden />
          </button>
        ))}
      </div>
      <span>Colour</span>
      <div className="flex flex-wrap gap-1.5">
        {CUSTOM_FLAG_COLORS.map((c) => (
          <button key={c} type="button" aria-label={`Colour ${c}`} aria-pressed={color === c} onClick={() => setColor(c)}
            className={`h-6 w-6 rounded-full ${color === c ? 'outline outline-2 outline-offset-1 outline-text-primary' : ''}`} style={{ background: c }} />
        ))}
      </div>
      {!builtIn && (
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={addressable} onChange={(e) => setAddressable(e.target.checked)} />Can be addressed
        </label>
      )}
      {!builtIn && addressable && (
        <label className="flex flex-col gap-1">Reply is called
          <input className={field} value={replyLabel} maxLength={40} onChange={(e) => setReplyLabel(e.target.value)} />
        </label>
      )}
      {taskRules && (
        <fieldset className="m-0 flex flex-col gap-1.5 rounded-md border border-border-hover px-2 pb-2 pt-0.5">
          <legend className="px-1 text-text-muted">When made into a task</legend>
          <div className="flex items-center gap-2">
            <span>Priority</span>
            <select aria-label="Task priority" className="rounded-md border border-border-hover bg-bg-input px-1.5 py-0.5 text-sm text-text-primary"
              value={priority} onChange={(e) => setPriority(e.target.value as TaskPriorityRule | '')}>
              <option value="">Default ({taskPriorityLabel(defaults.priority)})</option>
              {TASK_PRIORITY_RULES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" aria-label="Add a tag with the flag's name" checked={tag} onChange={(e) => setTag(e.target.checked)} />
            Add a tag with the flag's name<span className="text-text-muted">(default {defaults.tag ? 'on' : 'off'})</span>
          </label>
          {personType && (
            <label className="flex items-center gap-1.5">
              <input type="checkbox" aria-label="Add a follow-up with the person" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} />
              Add a follow-up with the person<span className="text-text-muted">(default {defaults.followUp ? 'on' : 'off'})</span>
            </label>
          )}
        </fieldset>
      )}
      {error && <p className="m-0 text-danger" role="alert">{error}</p>}
      <div className="flex gap-1.5">
        <button type="submit" disabled={busy} className="rounded-md bg-accent px-3 py-1 text-sm text-white hover:bg-accent-hover disabled:opacity-50">{submitLabel}</button>
        <button type="button" onClick={onCancel} className="rounded-md px-3 py-1 text-sm text-text-secondary hover:bg-bg-hover">Cancel</button>
      </div>
    </form>
  );
}
